import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Loader2, Plus, ArrowLeft, ScanBarcode } from "lucide-react";
import BarcodeScanner from "@/components/food/BarcodeScanner";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

const round1 = (n) => Math.round(n * 10) / 10;

export const FoodSearchModal = ({ date, meal, onClose, onLogged }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recents, setRecents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [offError, setOffError] = useState(false);
  const [picked, setPicked] = useState(null); // food awaiting qty
  const [qty, setQty] = useState("100");
  const [showCustom, setShowCustom] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [looking, setLooking] = useState(false);
  const [custom, setCustom] = useState({ name: "", serving_desc: "", calories: "", protein_g: "", carbs_g: "", fat_g: "" });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/food/recent").then((r) => setRecents(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/food/search", { params: { q: query.trim() } });
        setResults(r.data.results || []);
        setOffError(Boolean(r.data.off_error));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const pick = (f) => {
    setPicked(f);
    if (f.source === "custom") setQty("1");
    else if (f.source === "recent") setQty("1");
    else setQty(String(parseFloat(f.serving_quantity) || 100));
  };

  const onBarcodeDetected = useCallback(async (code) => {
    setScanning(false);
    setLooking(true);
    try {
      const r = await api.get(`/food/barcode/${encodeURIComponent(code)}`);
      toast.success(`Found: ${r.data.name}`);
      setPicked(r.data);
      setQty(String(parseFloat(r.data.serving_quantity) || 100));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Barcode lookup failed");
    } finally {
      setLooking(false);
    }
  }, []);

  const computed = () => {
    if (!picked) return null;
    const n = parseFloat(qty) || 0;
    if (picked.source === "recent") {
      return { ...picked, factor: 1 };
    }
    const factor = picked.basis === "per_100g" ? n / 100 : n;
    return {
      calories: round1(picked.calories * factor),
      protein_g: round1(picked.protein_g * factor),
      carbs_g: round1(picked.carbs_g * factor),
      fat_g: round1(picked.fat_g * factor),
    };
  };

  const logPicked = async () => {
    const c = computed();
    if (!c) return;
    const qtyDesc =
      picked.source === "recent"
        ? picked.qty_desc || ""
        : picked.basis === "per_100g"
        ? `${qty} g`
        : `${qty} × ${picked.serving_desc || "serving"}`;
    setSaving(true);
    try {
      await api.post("/food/log", {
        date,
        meal,
        name: picked.name,
        brand: picked.brand || null,
        qty_desc: qtyDesc,
        calories: c.calories,
        protein_g: c.protein_g,
        carbs_g: c.carbs_g,
        fat_g: c.fat_g,
        source: picked.source === "recent" ? "recent" : picked.source,
      });
      toast.success(`Logged ${picked.name}`);
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  const saveCustom = async () => {
    if (!custom.name.trim() || !custom.serving_desc.trim()) {
      toast.error("Name and serving size are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/food/custom", {
        name: custom.name,
        serving_desc: custom.serving_desc,
        calories: parseFloat(custom.calories) || 0,
        protein_g: parseFloat(custom.protein_g) || 0,
        carbs_g: parseFloat(custom.carbs_g) || 0,
        fat_g: parseFloat(custom.fat_g) || 0,
      });
      toast.success("Custom food saved");
      setShowCustom(false);
      pick({ ...r.data, source: "custom", basis: "per_serving" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save food");
    } finally {
      setSaving(false);
    }
  };

  const list = query.trim().length >= 2 ? results : recents;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="food-search-modal">
      <div className="obs-card w-full max-w-lg my-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-blaze">/ {meal}</div>
            <h3 className="font-heading text-2xl uppercase">Add food</h3>
          </div>
          <button onClick={onClose} data-testid="food-modal-close" className="text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* STEP 2 — quantity */}
        {picked ? (
          <div className="space-y-4" data-testid="food-qty-step">
            <button onClick={() => setPicked(null)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft className="h-3 w-3" /> Back to search
            </button>
            <div>
              <div className="font-heading text-xl uppercase">{picked.name}</div>
              {picked.brand && <div className="text-xs text-zinc-500">{picked.brand}</div>}
            </div>
            {picked.source !== "recent" && (
              <div>
                <div className="overline mb-1.5">
                  {picked.basis === "per_100g" ? "Amount (grams)" : `Servings (${picked.serving_desc || "serving"})`}
                </div>
                <input
                  data-testid="food-qty-input"
                  type="number"
                  min="0"
                  step="any"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className={inputCls}
                />
                {picked.basis === "per_100g" && picked.serving_size && (
                  <div className="text-[11px] text-zinc-500 mt-1">Package serving: {picked.serving_size}</div>
                )}
              </div>
            )}
            {(() => {
              const c = computed();
              return (
                <div className="grid grid-cols-4 gap-2 text-center" data-testid="food-qty-macros">
                  <div className="stat-tile !p-2"><div className="overline">kcal</div><div className="font-data text-lg">{c.calories}</div></div>
                  <div className="stat-tile !p-2"><div className="overline">P</div><div className="font-data text-lg text-blaze">{c.protein_g}</div></div>
                  <div className="stat-tile !p-2"><div className="overline">C</div><div className="font-data text-lg">{c.carbs_g}</div></div>
                  <div className="stat-tile !p-2"><div className="overline">F</div><div className="font-data text-lg">{c.fat_g}</div></div>
                </div>
              );
            })()}
            <button data-testid="food-log-confirm" onClick={logPicked} disabled={saving} className="blaze-btn w-full py-2.5 text-xs disabled:opacity-50">
              {saving ? "Logging…" : `Log to ${meal}`}
            </button>
          </div>
        ) : scanning ? (
          /* BARCODE SCANNER */
          <BarcodeScanner onDetected={onBarcodeDetected} onClose={() => setScanning(false)} />
        ) : showCustom ? (
          /* CUSTOM FOOD FORM */
          <div className="space-y-3" data-testid="food-custom-form">
            <button onClick={() => setShowCustom(false)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
            <input data-testid="custom-food-name" placeholder="Food name" value={custom.name} onChange={(e) => setCustom((c) => ({ ...c, name: e.target.value }))} className={inputCls} />
            <input data-testid="custom-food-serving" placeholder="Serving size (e.g. 1 scoop, 250 ml)" value={custom.serving_desc} onChange={(e) => setCustom((c) => ({ ...c, serving_desc: e.target.value }))} className={inputCls} />
            <div className="grid grid-cols-4 gap-2">
              {["calories", "protein_g", "carbs_g", "fat_g"].map((k, i) => (
                <div key={k}>
                  <div className="overline mb-1">{["kcal", "P (g)", "C (g)", "F (g)"][i]}</div>
                  <input
                    data-testid={`custom-food-${k}`}
                    type="number" min="0" step="any"
                    value={custom[k]}
                    onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              ))}
            </div>
            <button data-testid="custom-food-save" onClick={saveCustom} disabled={saving} className="blaze-btn w-full py-2.5 text-xs disabled:opacity-50">
              {saving ? "Saving…" : "Save & select"}
            </button>
          </div>
        ) : (
          /* STEP 1 — search */
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="food-search-input"
                autoFocus
                placeholder="Search foods (e.g. greek yogurt)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className={`${inputCls} pl-9`}
              />
              {searching && <Loader2 className="h-4 w-4 text-blaze animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {offError && (
              <p className="text-[11px] text-yellow-500">Food database is slow right now — showing your foods only. Try again shortly.</p>
            )}
            {query.trim().length < 2 && recents.length > 0 && <div className="overline">Recent</div>}
            <div className="max-h-72 overflow-y-auto divide-y divide-obsidian-600/50" data-testid="food-search-results">
              {list.map((f, i) => (
                <button
                  key={i}
                  data-testid={`food-result-${i}`}
                  onClick={() => pick(f)}
                  className="w-full text-left py-2.5 px-1 hover:bg-obsidian-900/60 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm truncate">
                        {f.name}
                        {f.source === "custom" && <span className="text-[10px] text-blaze uppercase tracking-widest ml-2">My food</span>}
                        {f.source === "recent" && <span className="text-[10px] text-emerald-400 uppercase tracking-widest ml-2">Recent</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {f.brand ? `${f.brand} · ` : ""}
                        {f.source === "custom" ? f.serving_desc : f.source === "recent" ? f.qty_desc : "per 100 g"}
                      </div>
                    </div>
                    <div className="font-data text-xs text-zinc-400 shrink-0">
                      {f.calories} kcal · <span className="text-blaze">{f.protein_g}P</span>
                    </div>
                  </div>
                </button>
              ))}
              {query.trim().length >= 2 && !searching && list.length === 0 && (
                <div className="text-zinc-500 text-sm py-6 text-center">No matches found.</div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                data-testid="food-scan-barcode-btn"
                onClick={() => setScanning(true)}
                disabled={looking}
                className="blaze-btn flex-1 py-2 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {looking ? <Loader2 className="h-3 w-3 animate-spin" /> : <ScanBarcode className="h-3.5 w-3.5" />}
                {looking ? "Looking up…" : "Scan barcode"}
              </button>
              <button
                data-testid="food-create-custom-btn"
                onClick={() => setShowCustom(true)}
                className="ghost-btn flex-1 py-2 text-xs inline-flex items-center justify-center gap-2"
              >
                <Plus className="h-3 w-3" /> Custom food
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodSearchModal;
